import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';

// Define the entity types based on the backend structure
export type AutoIsoEntityType = 'Player' | 'BallOrPuck';

export type PlayerPosition = 'C' | 'LW' | 'RW' | 'D' | 'G';
export type PlayerCategory = 'Forward' | 'Defense' | 'Goalie';

export interface BaseEntity {
  id: number;
  displayName: string;
  type: AutoIsoEntityType;
}

export interface PlayerEntity extends BaseEntity {
  type: 'Player';
  playerTeamId: number;
  playerPosition: PlayerPosition;
  playerNumber: string;
  playerCategory: PlayerCategory;
  headshot?: string;
}

export interface PuckEntity extends BaseEntity {
  type: 'BallOrPuck';
}

export type AutoIsoEntity = PlayerEntity | PuckEntity;

// Fetch function for tracked entity
export const fetchTrackedEntity = async (
  gameId: string,
  entityId: number,
): Promise<AutoIsoEntity> => {
  try {
    const response = await get<AutoIsoEntity>(
      `/v1.0/games/${gameId}/Lookup?entityId=${entityId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Failed to fetch tracked entity:', error);
    throw error;
  }
};

// Custom hook to fetch tracked entity
const useTrackedEntityQuery = (
  gameId?: string,
  entityId?: number,
): UseQueryResult<AutoIsoEntity, Error> => {
  return useQuery({
    queryKey: ['trackedEntity', gameId, entityId],
    queryFn: () => fetchTrackedEntity(gameId!, entityId!),
    enabled: !!gameId && !!entityId && entityId > 0, // Only fetch if both parameters are provided and valid
    staleTime: 1000 * 60 * 30, // 30 minutes - entities don't change often
    gcTime: 1000 * 60 * 60, // 60 minutes
    retry: 1,
    retryDelay: 1000,
  });
};

export default useTrackedEntityQuery;
